import { useState, useEffect } from 'react';
import { Phone, MessageCircle, ArrowRight, ArrowLeft, Upload, CheckCircle, Home, Bed, Calendar, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { companyInfo, rewirePricing, startingFromPrice } from '../data/pricing';
import { trackRewiringEvents, captureTrackingParams, getTrackingParams, initializeTracking } from '../utils/tracking';
import { SEO } from '../components/SEO';

export const RewireQuote = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    postcode: '',
    propertyType: '',
    bedrooms: '',
    propertyStatus: '',
    rewireType: '',
    timeframe: '',
    name: '',
    mobile: '',
    email: '',
    message: '',
    photos: null
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [trackingData, setTrackingData] = useState(null);

  useEffect(() => {
    // Initialize tracking and capture UTM params
    initializeTracking();
    const params = getTrackingParams();
    setTrackingData(params);
    
    // Track page view
    trackRewiringEvents.pageView('/rewire-quote');
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step === 1) {
      trackRewiringEvents.quoteStarted();
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      // Prepare lead data with tracking
      const leadData = {
        postcode: formData.postcode,
        propertyType: formData.propertyType,
        bedrooms: formData.bedrooms,
        propertyStatus: formData.propertyStatus,
        rewireType: formData.rewireType,
        timeframe: formData.timeframe,
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        message: formData.message || null,
        tracking: trackingData
      };

      // Send to backend API
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rewire-leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote request');
      }

      const result = await response.json();
      
      // Only track conversion AFTER successful backend submission (200 OK)
      trackRewiringEvents.quoteSubmitted({
        propertyType: formData.propertyType,
        bedrooms: formData.bedrooms,
        postcode: formData.postcode,
        estimatedValue: 5000
      });

      console.log('✅ Rewire Quote Submitted:', result);
      setSubmitted(true);
      
    } catch (error) {
      console.error('❌ Quote submission error:', error);
      setSubmitError(error.message || 'Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhoneClick = () => {
    trackRewiringEvents.phoneClick('rewire_quote_page');
  };

  const handleWhatsAppClick = () => {
    trackRewiringEvents.whatsappClick('rewire_quote_page');
  };

  const totalSteps = 7;
  const progress = (step / totalSteps) * 100;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SEO 
          title="Quote Submitted - Crescent Electrical"
          description="Thank you for your rewiring quote request"
          canonical="https://crescentelectrical.co.uk/rewire-quote"
        />
        
        <section className="py-20">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-3xl text-gray-900">Quote Request Received!</CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-6">
                <p className="text-lg text-gray-700">
                  Thank you, <strong>{formData.name}</strong>. We've received your rewiring quote request.
                </p>
                
                <div className="bg-white rounded-lg p-6 border border-green-200">
                  <h3 className="font-semibold text-gray-900 mb-4">What happens next?</h3>
                  <div className="space-y-3 text-left">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        1
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">We'll review your property details</p>
                        <p className="text-sm text-gray-600">Within 2-4 hours during business hours</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        2
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Initial phone consultation</p>
                        <p className="text-sm text-gray-600">We'll call you on {formData.mobile}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        3
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Site survey (if required)</p>
                        <p className="text-sm text-gray-600">For accurate pricing</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                        4
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Written quotation</p>
                        <p className="text-sm text-gray-600">Fixed price with full breakdown</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Need to speak to us now?</strong>
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 mt-4 justify-center">
                    <a href={`tel:${companyInfo.phone}`} onClick={handlePhoneClick}>
                      <Button className="gap-2 bg-blue-600 hover:bg-blue-700">
                        <Phone className="h-4 w-4" />
                        Call {companyInfo.phone}
                      </Button>
                    </a>
                    <a 
                      href={`https://wa.me/${companyInfo.whatsapp}?text=Hi%20Crescent%20Electrical%2C%20I%20just%20submitted%20a%20rewire%20quote%20request`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleWhatsAppClick}
                    >
                      <Button variant="outline" className="gap-2">
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp Us
                      </Button>
                    </a>
                  </div>
                </div>

                <p className="text-sm text-gray-600">
                  Reference: {formData.postcode.toUpperCase()} - {new Date().toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Get Your Rewiring Quote - Crescent Electrical Ltd"
        description="Professional house and flat rewiring across London. Get your fixed price quote today. Rewires from £4,950."
        canonical="https://crescentelectrical.co.uk/rewire-quote"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">
              House & Flat Rewiring
            </h1>
            <p className="text-xl text-blue-100 mb-2">
              Professional Full & Partial Rewires
            </p>
            <div className="text-3xl font-bold mb-2">
              REWIRES FROM {startingFromPrice}*
            </div>
            <p className="text-sm text-blue-100 max-w-2xl mx-auto">
              *Current starting price applies to 1–2 bedroom flats. Final quotation depends on property type, 
              specification, access and requirements.
            </p>
          </div>

          {/* Quick Contact */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <a href={`tel:${companyInfo.phone}`} onClick={handlePhoneClick}>
              <Button size="lg" className="w-full sm:w-auto gap-2 bg-white text-blue-600 hover:bg-gray-100">
                <Phone className="h-5 w-5" />
                Call Now
              </Button>
            </a>
            <a 
              href={`https://wa.me/${companyInfo.whatsapp}?text=Hi%20Crescent%20Electrical%2C%20I%27m%20looking%20for%20a%20quote%20for%20a%20property%20rewire`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleWhatsAppClick}
            >
              <Button size="lg" variant="outline" className="w-full sm:w-auto gap-2 border-white text-white hover:bg-blue-700">
                <MessageCircle className="h-5 w-5" />
                WhatsApp Quote
              </Button>
            </a>
          </div>

          {/* Pricing Table */}
          <Card className="max-w-3xl mx-auto bg-white/95 backdrop-blur">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 text-center mb-4">Current Rewire Pricing</h3>
              <div className="space-y-3">
                {rewirePricing.map((item) => (
                  <div key={item.id} className="flex justify-between items-center pb-3 border-b border-gray-200 last:border-0">
                    <span className="text-gray-700 font-medium">{item.propertyType}</span>
                    <span className="text-blue-600 font-bold text-lg">{item.price}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 bg-orange-50 rounded-lg p-3">
                <p className="text-sm text-gray-700 text-center">
                  <strong>Occupied properties:</strong> Additional £400–£800 depending on furniture, access and protection required
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <Card className="border-blue-200 shadow-xl">
            <CardHeader>
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-2xl">Get Your Rewire Quote</CardTitle>
                <span className="text-sm text-gray-500 font-semibold">Step {step} of {totalSteps}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit}>
                {/* Step 1: Postcode */}
                {step === 1 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <Home className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                      <h3 className="text-xl font-semibold text-gray-900">Where is the property?</h3>
                      <p className="text-gray-600 mt-2">We cover all of Greater London</p>
                    </div>
                    <div>
                      <Label htmlFor="postcode" className="text-base font-semibold mb-2 block">Postcode</Label>
                      <Input
                        id="postcode"
                        type="text"
                        placeholder="e.g. N18 1PX"
                        value={formData.postcode}
                        onChange={(e) => handleChange('postcode', e.target.value.toUpperCase())}
                        className="text-lg py-6"
                        required
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Property Type */}
                {step === 2 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <Home className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                      <h3 className="text-xl font-semibold text-gray-900">What type of property?</h3>
                    </div>
                    <RadioGroup value={formData.propertyType} onValueChange={(val) => handleChange('propertyType', val)} required>
                      {['Flat', 'Terraced', 'Semi-Detached', 'Detached', 'Other'].map((type) => (
                        <div key={type} className="flex items-center space-x-2 border-2 rounded-lg p-4 hover:bg-gray-50 cursor-pointer hover:border-blue-300">
                          <RadioGroupItem value={type.toLowerCase()} id={type} />
                          <Label htmlFor={type} className="flex-1 cursor-pointer font-medium">{type}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Step 3: Bedrooms */}
                {step === 3 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <Bed className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                      <h3 className="text-xl font-semibold text-gray-900">How many bedrooms?</h3>
                    </div>
                    <RadioGroup value={formData.bedrooms} onValueChange={(val) => handleChange('bedrooms', val)} required>
                      <div className="grid grid-cols-2 gap-3">
                        {['Studio', '1', '2', '3', '4', '5', '6+'].map((bed) => (
                          <div key={bed} className="flex items-center space-x-2 border-2 rounded-lg p-4 hover:bg-gray-50 cursor-pointer hover:border-blue-300">
                            <RadioGroupItem value={bed} id={`bed-${bed}`} />
                            <Label htmlFor={`bed-${bed}`} className="flex-1 cursor-pointer font-medium">
                              {bed} {bed !== 'Studio' && bed !== '6+' ? 'Bed' : ''}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>
                )}

                {/* Step 4: Property Status */}
                {step === 4 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <Home className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                      <h3 className="text-xl font-semibold text-gray-900">What's the property status?</h3>
                    </div>
                    <RadioGroup value={formData.propertyStatus} onValueChange={(val) => handleChange('propertyStatus', val)} required>
                      {[
                        { value: 'vacant', label: 'Vacant / Empty' },
                        { value: 'occupied', label: 'Occupied' },
                        { value: 'refurbishment', label: 'Being Refurbished' },
                        { value: 'purchased', label: 'Recently Purchased' },
                        { value: 'planning', label: 'Planning to Purchase' }
                      ].map((status) => (
                        <div key={status.value} className="flex items-center space-x-2 border-2 rounded-lg p-4 hover:bg-gray-50 cursor-pointer hover:border-blue-300">
                          <RadioGroupItem value={status.value} id={status.value} />
                          <Label htmlFor={status.value} className="flex-1 cursor-pointer font-medium">{status.label}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Step 5: Work Required */}
                {step === 5 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <Zap className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                      <h3 className="text-xl font-semibold text-gray-900">What work do you need?</h3>
                    </div>
                    <RadioGroup value={formData.rewireType} onValueChange={(val) => handleChange('rewireType', val)} required>
                      {[
                        { value: 'full', label: 'Full Rewire', desc: 'Complete property rewiring' },
                        { value: 'partial', label: 'Partial Rewire', desc: 'Specific rooms or circuits' },
                        { value: 'unsure', label: 'Not Sure', desc: 'Need advice on what I need' }
                      ].map((work) => (
                        <div key={work.value} className="flex items-start space-x-3 border-2 rounded-lg p-4 hover:bg-gray-50 cursor-pointer hover:border-blue-300">
                          <RadioGroupItem value={work.value} id={work.value} className="mt-1" />
                          <div className="flex-1">
                            <Label htmlFor={work.value} className="cursor-pointer font-medium block">{work.label}</Label>
                            <p className="text-sm text-gray-600">{work.desc}</p>
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Step 6: Timeframe */}
                {step === 6 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <Calendar className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                      <h3 className="text-xl font-semibold text-gray-900">When do you need the work?</h3>
                    </div>
                    <RadioGroup value={formData.timeframe} onValueChange={(val) => handleChange('timeframe', val)} required>
                      {[
                        'ASAP',
                        'Within 1 month',
                        '1–3 months',
                        '3+ months',
                        'Just researching'
                      ].map((time) => (
                        <div key={time} className="flex items-center space-x-2 border-2 rounded-lg p-4 hover:bg-gray-50 cursor-pointer hover:border-blue-300">
                          <RadioGroupItem value={time} id={time} />
                          <Label htmlFor={time} className="flex-1 cursor-pointer font-medium">{time}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                )}

                {/* Step 7: Contact Details */}
                {step === 7 && (
                  <div className="space-y-4">
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-semibold text-gray-900">Your contact details</h3>
                      <p className="text-gray-600 mt-2">We'll send your quote to these details</p>
                    </div>
                    
                    {submitError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <p className="text-red-800 text-sm font-medium">⚠️ {submitError}</p>
                        <p className="text-red-700 text-sm mt-1">
                          Please try again or call us directly on {companyInfo.phone}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <Label htmlFor="name" className="font-semibold mb-2 block">Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="py-6"
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="mobile" className="font-semibold mb-2 block">Mobile *</Label>
                      <Input
                        id="mobile"
                        type="tel"
                        placeholder="07XXX XXXXXX"
                        value={formData.mobile}
                        onChange={(e) => handleChange('mobile', e.target.value)}
                        className="py-6"
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="font-semibold mb-2 block">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="py-6"
                        required
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="font-semibold mb-2 block">
                        Additional Information <span className="text-gray-500 font-normal">(Optional)</span>
                      </Label>
                      <Textarea
                        id="message"
                        placeholder="Tell us more about your project, any specific requirements, preferred dates, etc."
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        rows={4}
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-gray-700">
                      <CheckCircle className="h-5 w-5 text-blue-600 inline mr-2" />
                      Your information will only be used to provide your rewiring quote
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 mt-4"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Get My Quote'}
                    </Button>

                    <p className="text-xs text-gray-600 text-center mt-3">
                      We'll use your details to prepare your quotation. See our{' '}
                      <a href="/privacy" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a>
                      {' '}for more information.
                    </p>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex gap-3 mt-8">
                  {step > 1 && (
                    <Button type="button" variant="outline" onClick={handleBack} className="flex-1 py-6">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                  )}
                  {step < totalSteps ? (
                    <Button 
                      type="button"
                      onClick={handleNext}
                      disabled={
                        (step === 1 && !formData.postcode) ||
                        (step === 2 && !formData.propertyType) ||
                        (step === 3 && !formData.bedrooms) ||
                        (step === 4 && !formData.propertyStatus) ||
                        (step === 5 && !formData.rewireType) ||
                        (step === 6 && !formData.timeframe)
                      }
                      className="flex-1 bg-blue-600 hover:bg-blue-700 py-6"
                    >
                      Next
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button 
                      type="submit"
                      disabled={!formData.name || !formData.mobile || !formData.email || isSubmitting}
                      className="flex-1 bg-green-600 hover:bg-green-700 py-6 text-lg font-semibold disabled:opacity-50"
                    >
                      {isSubmitting ? 'Submitting...' : 'Request My Rewire Quote'}
                      {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <div className="flex flex-wrap justify-center gap-4 items-center text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>NICEIC Approved</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>5.0 Google Rated</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>Fully Insured</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
